import { TradingRules } from '../types';

export const defaultGlobalRules: Omit<TradingRules, 'id' | 'createdAt' | 'updatedAt'> = {
  name: 'Default Global Rules',
  description: 'Default trading rules for all portfolios. Can be overridden at portfolio level.',
  isGlobal: true,
  
  // Position Management - targeting 3-5 rotating stocks
  maxHoldingDays: 21, // 3 weeks maximum holding period
  defaultTrancheCount: 4, // Default to 4 equal positions
  positionCooldownDays: 21, // Wait 3 weeks before re-entering same stock
  
  // Entry Rules - conservative approach for near-pivot entries
  maxDistanceFromPivot: 3.0, // Enter within 3% of pivot
  minVolumeIncrease: 10, // Require 10% volume increase (temporarily lowered from 50%)
  requirePositiveTrend: true, // Stock must be in uptrend
  minEntryScore: 60, // Minimum score threshold for entry recommendations (temporarily lowered from 70)
  minDayPriceChange: 0, // 0 = disabled (any price change acceptable)
  minTrackedPriceChange: 0, // 0 = disabled (any price change since tracking acceptable)
  
  // Exit Rules - standard profit/loss targets
  stopLossPercentage: 5.0, // 5% stop loss from entry
  targetGainPercentage: 10.0, // 10% profit target from pivot
  trailingStopActivation: 7.0, // Activate trailing stop at 7% gain
  
  // Adjustment Rules - for position management
  targetAdjustmentTrigger: 10.0, // Suggest target adjustment when up 10%
  targetAdjustmentThreshold: 10.0, // If target is still 10%+ away
  targetAdjustmentBuffer: 5.0, // New target 5% above current price
  trailingStopBuffer: 1.0, // Trailing stop 1% above entry
  
  // Risk Management
  maxSectorConcentration: 50, // No more than 50% in one sector
  minDaysBetweenSameStock: 21, // Cooldown period
  
  // Moderate approach by default
  aggressiveness: 'moderate',
};

// Conservative variant for risk-averse portfolios
export const conservativeRules: Partial<TradingRules> = {
  name: 'Conservative Rules',
  description: 'More conservative approach with tighter risk controls',
  
  maxHoldingDays: 14, // Exit sooner
  maxDistanceFromPivot: 2.0, // Tighter entry
  stopLossPercentage: 3.0, // Tighter stop
  targetGainPercentage: 7.0, // Lower profit target
  minVolumeIncrease: 75, // Higher volume requirement
  minEntryScore: 70, // Conservative: higher score requirement
  minDayPriceChange: 0.5, // Require some positive price movement
  minTrackedPriceChange: 0, // No minimum since tracking
  
  // Conservative adjustment rules
  targetAdjustmentTrigger: 7.0, // Trigger sooner at 7% gain
  targetAdjustmentBuffer: 3.0, // Tighter 3% buffer
  
  aggressiveness: 'conservative',
};

// Aggressive variant for higher risk tolerance
export const aggressiveRules: Partial<TradingRules> = {
  name: 'Aggressive Rules',
  description: 'More aggressive approach for experienced traders',
  
  maxHoldingDays: 28, // Hold longer
  defaultTrancheCount: 6, // More positions
  maxDistanceFromPivot: 5.0, // Wider entry range
  stopLossPercentage: 7.0, // Wider stop
  targetGainPercentage: 15.0, // Higher profit target
  minVolumeIncrease: 25, // Lower volume requirement
  minEntryScore: 50, // Aggressive: lower score requirement
  minDayPriceChange: -1, // Allow slightly negative price movement
  minTrackedPriceChange: -5, // Allow stocks down up to 5% since tracking
  maxSectorConcentration: 75, // Allow more concentration
  
  // Aggressive adjustment rules
  targetAdjustmentTrigger: 15.0, // Wait for 15% gain before adjusting
  targetAdjustmentBuffer: 7.0, // Wider 7% buffer
  
  aggressiveness: 'aggressive',
};

// Scoring weights for recommendation engine
export const scoringWeights = {
  entry: {
    technical: 0.40,
    portfolioFit: 0.30,
    timing: 0.20,
    risk: 0.10,
  },
  exit: {
    profitLoss: 0.40,
    timeHeld: 0.30,
    technicalDeterioration: 0.20,
    opportunityCost: 0.10,
  },
};

// Technical factors and their ideal values
export const technicalFactors = {
  distanceFromPivot: {
    ideal: 0, // At pivot
    acceptable: 3.0, // Within 3%
    weight: 0.25,
  },
  volumeChange: {
    ideal: 100, // 100% increase
    acceptable: 50, // 50% minimum
    weight: 0.25,
  },
  baseStage: {
    ideal: ['1', '2'], // Stage 1 or 2
    acceptable: ['1', '2', '3'],
    weight: 0.20,
  },
  baseType: {
    preferred: ['Cup With Handle', 'Cup Without Handle', 'Flat Base'],
    weight: 0.15,
  },
  percentOffHigh: {
    ideal: 5, // Within 5% of high
    acceptable: 10,
    weight: 0.15,
  },
};